import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { type OperatorRecord } from './OperatorImporterView';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface PinRow {
  id: string;
  name: string;
  role: string;
  pin: string;
  error: string;
}

interface Props {
  open: boolean;
  operators: OperatorRecord[];
  onClose: () => void;
  onSave: (
    data: Array<{ name: string; role: string | null; pin: number }>
  ) => Promise<void>;
}

const PIN_REGEX = /^\d{6}$/;

export const UpdatePinModal = ({
  open,
  operators,
  onClose,
  onSave,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PinRow[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(
        operators.map((op) => ({
          id: op.id,
          name: op.operatorName,
          role: op.role,
          pin: op.pin,
          error: '',
        }))
      );
    }
  }, [open, operators]);

  const updatePin = (id: string, value: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, pin: value, error: '' } : r))
    );
  };

  const validate = (): boolean => {
    const pinCounts: Record<string, number> = {};
    const next = rows.map((r) => {
      let error = '';
      const trimmed = r.pin.trim();
      if (!trimmed) {
        error = t('operator_importer_err_required');
      } else if (!PIN_REGEX.test(trimmed)) {
        error = t('operator_importer_pin_format_error');
      } else {
        pinCounts[trimmed] = (pinCounts[trimmed] ?? 0) + 1;
      }
      return { ...r, pin: trimmed, error };
    });

    // mark duplicates
    const withDupes = next.map((r) => {
      if (!r.error && pinCounts[r.pin] > 1) {
        return { ...r, error: t('operator_importer_pin_duplicate_error') };
      }
      return r;
    });

    setRows(withDupes);
    return withDupes.every((r) => r.error === '');
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(
        rows.map((r) => ({
          name: r.name,
          role: r.role !== '' ? r.role : null,
          pin: parseInt(r.pin, 10),
        }))
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.primary.main, 0.25),
            }}
          >
            <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('operator_importer_update_pin_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('operator_importer_update_pin_subtitle')}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        <TableContainer
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.divider, 0.6),
            maxHeight: 400,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: 'background.paper',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  {t('operator_importer_col_operator_name')}
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: 'background.paper',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    width: 160,
                  }}
                >
                  {t('operator_importer_col_pin')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.name}
                    </Typography>
                    {row.role && (
                      <Typography variant="caption" color="text.secondary">
                        {row.role}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={row.pin}
                      onChange={(e) => {
                        updatePin(row.id, e.target.value);
                      }}
                      error={!!row.error}
                      helperText={row.error}
                      placeholder="000000"
                      inputProps={{
                        maxLength: 6,
                        pattern: '[0-9]*',
                        inputMode: 'numeric',
                      }}
                      sx={{ width: 130 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1.5, display: 'block' }}
        >
          {t('operator_importer_pin_hint')}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (t) => alpha(t.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={saving}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('operator_importer_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSave();
          }}
          disabled={saving || rows.length === 0}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('operator_importer_save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
